import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import './SupportTickets.css';

const SupportTickets = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Placeholder support tickets data
  const allTickets = [
    {
      id: 'TKT001',
      userId: 'USR001',
      userName: 'John Doe',
      subject: 'Flight cancellation request',
      submittedDate: '2024-12-10',
      status: 'Open',
      priority: 'High',
      assignedAgent: 'Sarah Johnson'
    },
    {
      id: 'TKT002',
      userId: 'USR002',
      userName: 'Jane Smith',
      subject: 'Baggage inquiry',
      submittedDate: '2024-12-11',
      status: 'Pending',
      priority: 'Medium',
      assignedAgent: 'Michael Brown'
    },
    {
      id: 'TKT003',
      userId: 'USR003',
      userName: 'Michael Brown',
      subject: 'Refund status',
      submittedDate: '2024-12-09',
      status: 'Resolved',
      priority: 'High',
      assignedAgent: 'Sarah Johnson'
    },
    {
      id: 'TKT004',
      userId: 'USR004',
      userName: 'Sarah Johnson',
      subject: 'Seat selection issue',
      submittedDate: '2024-12-12',
      status: 'Open',
      priority: 'Low',
      assignedAgent: 'Unassigned'
    },
    {
      id: 'TKT005',
      userId: 'USR005',
      userName: 'David Williams',
      subject: 'Payment problem',
      submittedDate: '2024-12-08',
      status: 'Closed',
      priority: 'High',
      assignedAgent: 'Michael Brown'
    },
    {
      id: 'TKT006',
      userId: 'USR006',
      userName: 'Emily Davis',
      subject: 'Booking modification',
      submittedDate: '2024-12-13',
      status: 'Pending',
      priority: 'Medium',
      assignedAgent: 'Sarah Johnson'
    },
    {
      id: 'TKT007',
      userId: 'USR007',
      userName: 'Robert Miller',
      subject: 'Flight delay compensation',
      submittedDate: '2024-12-07',
      status: 'Open',
      priority: 'High',
      assignedAgent: 'Unassigned'
    },
    {
      id: 'TKT008',
      userId: 'USR008',
      userName: 'Lisa Anderson',
      subject: 'Luggage lost',
      submittedDate: '2024-12-14',
      status: 'Open',
      priority: 'High',
      assignedAgent: 'Michael Brown'
    },
    {
      id: 'TKT009',
      userId: 'USR009',
      userName: 'James Wilson',
      subject: 'General inquiry',
      submittedDate: '2024-12-06',
      status: 'Resolved',
      priority: 'Low',
      assignedAgent: 'Sarah Johnson'
    },
    {
      id: 'TKT010',
      userId: 'USR010',
      userName: 'Maria Garcia',
      subject: 'Check-in assistance',
      submittedDate: '2024-12-15',
      status: 'Pending',
      priority: 'Low',
      assignedAgent: 'Unassigned'
    },
    {
      id: 'TKT011',
      userId: 'USR011',
      userName: 'William Martinez',
      subject: 'Visa requirements',
      submittedDate: '2024-12-05',
      status: 'Resolved',
      priority: 'Medium',
      assignedAgent: 'Michael Brown'
    },
    {
      id: 'TKT012',
      userId: 'USR012',
      userName: 'Patricia Taylor',
      subject: 'Special meal request',
      submittedDate: '2024-12-04',
      status: 'Closed',
      priority: 'Low',
      assignedAgent: 'Sarah Johnson'
    }
  ];

  // Filter tickets based on search and filters
  const filteredTickets = allTickets.filter(ticket => {
    const matchesSearch = 
      ticket.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.subject.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      ticket.status.toLowerCase() === statusFilter.toLowerCase();
    
    const matchesPriority = priorityFilter === 'all' || 
      ticket.priority.toLowerCase() === priorityFilter.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredTickets.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedTickets = filteredTickets.slice(startIndex, endIndex);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePriorityFilterChange = (e) => {
    setPriorityFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleExport = () => {
    // Placeholder for export functionality
    console.log('Exporting tickets to CSV...');
  };

  const handleViewTicket = (ticketId) => {
    navigate(`/admin/support-tickets/${ticketId}`);
  };

  const handleAssignAgent = (ticketId) => {
    // Placeholder for assign/reassign agent
    console.log('Assign agent to ticket:', ticketId);
  };

  const handleChangeStatus = (ticketId) => {
    // Placeholder for change status
    console.log('Change status for ticket:', ticketId);
  };

  const handleCloseTicket = (ticketId) => {
    // Placeholder for close ticket
    console.log('Close ticket:', ticketId);
  };

  return (
    <AdminLayout pageTitle="Support Tickets">
      <div className="admin-support-tickets-page">
        {/* Page Title */}
        <div className="admin-support-tickets-header">
          <h1 className="admin-support-tickets-title">Support Tickets</h1>
          <p className="admin-support-tickets-subtitle">Manage customer inquiries and issues</p>
        </div>

        {/* Top Control Bar */}
        <div className="admin-support-tickets-controls">
          <div className="admin-controls-left">
            <div className="admin-search-wrapper">
              <input
                type="text"
                className="admin-search-input"
                placeholder="Search by Ticket ID, user, or subject..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="admin-search-icon">🔍</span>
            </div>
            
            <select
              className="admin-filter-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>

            <select
              className="admin-filter-select"
              value={priorityFilter}
              onChange={handlePriorityFilterChange}
            >
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>

            <select className="admin-filter-select">
              <option value="">Date Range</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          <div className="admin-controls-right">
            <button className="admin-export-button" onClick={handleExport}>
              <span className="admin-export-icon">📥</span>
              Export CSV
            </button>
          </div>
        </div>

        {/* Support Tickets Table */}
        {paginatedTickets.length > 0 ? (
          <>
            <div className="admin-support-tickets-table-wrapper">
              {/* Desktop Table */}
              <table className="admin-support-tickets-table">
                <thead>
                  <tr>
                    <th>Ticket ID</th>
                    <th>User</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTickets.map((ticket) => (
                    <tr key={ticket.id} className="admin-support-tickets-table-row">
                      <td className="admin-table-cell admin-table-id">{ticket.id}</td>
                      <td className="admin-table-cell">{ticket.userName}</td>
                      <td className="admin-table-cell admin-table-subject">{ticket.subject}</td>
                      <td className="admin-table-cell">
                        <span className={`admin-status-badge admin-status-${ticket.status.toLowerCase()}`}>
                          {ticket.status}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <span className={`admin-priority-badge admin-priority-${ticket.priority.toLowerCase()}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <div className="admin-actions-menu">
                          <button
                            className="admin-action-button"
                            onClick={() => handleViewTicket(ticket.id)}
                          >
                            View Ticket
                          </button>
                          <button
                            className="admin-action-button"
                            onClick={() => handleAssignAgent(ticket.id)}
                          >
                            {ticket.assignedAgent === 'Unassigned' ? 'Assign Agent' : 'Reassign'}
                          </button>
                          <button
                            className="admin-action-button"
                            onClick={() => handleChangeStatus(ticket.id)}
                          >
                            Change Status
                          </button>
                          {ticket.status !== 'Closed' && (
                            <button
                              className="admin-action-button"
                              onClick={() => handleCloseTicket(ticket.id)}
                            >
                              Close Ticket
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className="admin-support-tickets-cards">
                {paginatedTickets.map((ticket) => (
                  <div key={ticket.id} className="admin-ticket-card">
                    <div className="admin-ticket-card-header">
                      <div className="admin-ticket-card-id">{ticket.id}</div>
                      <div className="admin-ticket-card-badges">
                        <span className={`admin-status-badge admin-status-${ticket.status.toLowerCase()}`}>
                          {ticket.status}
                        </span>
                        <span className={`admin-priority-badge admin-priority-${ticket.priority.toLowerCase()}`}>
                          {ticket.priority}
                        </span>
                      </div>
                    </div>
                    <div className="admin-ticket-card-body">
                      <div className="admin-ticket-card-row">
                        <span className="admin-ticket-card-label">User:</span>
                        <span className="admin-ticket-card-value">{ticket.userName}</span>
                      </div>
                      <div className="admin-ticket-card-row">
                        <span className="admin-ticket-card-label">Subject:</span>
                        <span className="admin-ticket-card-value admin-ticket-card-subject">{ticket.subject}</span>
                      </div>
                      <div className="admin-ticket-card-row">
                        <span className="admin-ticket-card-label">Submitted:</span>
                        <span className="admin-ticket-card-value">{ticket.submittedDate}</span>
                      </div>
                      <div className="admin-ticket-card-row">
                        <span className="admin-ticket-card-label">Assigned Agent:</span>
                        <span className="admin-ticket-card-value">{ticket.assignedAgent}</span>
                      </div>
                    </div>
                    <div className="admin-ticket-card-actions">
                      <select
                        className="admin-mobile-actions-select"
                        onChange={(e) => {
                          const action = e.target.value;
                          if (action === 'view') handleViewTicket(ticket.id);
                          else if (action === 'assign') handleAssignAgent(ticket.id);
                          else if (action === 'status') handleChangeStatus(ticket.id);
                          else if (action === 'close' && ticket.status !== 'Closed') handleCloseTicket(ticket.id);
                          e.target.value = '';
                        }}
                      >
                        <option value="">Actions...</option>
                        <option value="view">View Ticket</option>
                        <option value="assign">{ticket.assignedAgent === 'Unassigned' ? 'Assign Agent' : 'Reassign Agent'}</option>
                        <option value="status">Change Status</option>
                        {ticket.status !== 'Closed' && (
                          <option value="close">Close Ticket</option>
                        )}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className="admin-pagination">
              <div className="admin-pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredTickets.length)} of {filteredTickets.length} tickets
              </div>
              <div className="admin-pagination-controls">
                <select
                  className="admin-rows-select"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <div className="admin-page-numbers">
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => {
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <span className="admin-page-ellipsis">...</span>}
                          <button
                            className={`admin-page-button ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="admin-empty-state">
            <div className="admin-empty-icon">💬</div>
            <h3 className="admin-empty-title">No tickets found</h3>
            <p className="admin-empty-text">
              {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'No support tickets have been submitted yet'}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SupportTickets;

